import { useId } from 'react';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Box, Typography } from '@mui/material';

import { useAuth } from '../../core/authContext';
import { requestSisConnection } from '../../core/setupStateApi';
import { feedbackSurveyUrl } from '../../core/surveyLink';
import { COLORS } from '../IndividualPlan/tokens';
import { REPORTS } from '../Reports/tokens';
import { GET_STARTED_URL, sampleBannerCopy, type SampleKind } from './sampleCopy';

const BUTTON_FONT = { fontFamily: 'inherit', fontSize: 16, lineHeight: '20px', fontWeight: 600 } as const;

export const TEXT_BUTTON_SX = {
  ...BUTTON_FONT,
  display: 'inline-flex',
  alignItems: 'center',
  height: 40,
  padding: '4px 12px',
  borderRadius: '8px',
  color: COLORS.navy,
  bgcolor: 'transparent',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  '&:hover': { bgcolor: REPORTS.welcome.bg },
  '&:active': { bgcolor: REPORTS.welcome.chipBg },
} as const;

export const PRIMARY_BUTTON_SX = {
  ...BUTTON_FONT,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  height: 40,
  padding: '0 20px',
  border: 0,
  borderRadius: '8px',
  color: '#FFFFFF',
  bgcolor: COLORS.navy,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  '&:hover': { bgcolor: COLORS.indigo },
} as const;

function recordSisRequest(): void {
  requestSisConnection().catch((err) => {
    console.warn('sample banner: SIS request not recorded', err);
  });
}

export function SampleBanner({
  kind,
  admin,
  sisConnected,
  accessUploaded = false,
  sisMatchingConfirmed = false,
  sisMatchingRequired = false,
  onUploadAccess,
}: {
  kind: SampleKind;
  admin: boolean;
  sisConnected: boolean;
  accessUploaded?: boolean;
  sisMatchingConfirmed?: boolean;
  sisMatchingRequired?: boolean;
  onUploadAccess?: () => void;
}) {
  const titleId = useId();
  const { user } = useAuth();
  const copy = sampleBannerCopy(kind, { admin, sisConnected, accessUploaded, sisMatchingConfirmed, sisMatchingRequired });
  return (
    <Box
      component="section"
      aria-labelledby={titleId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        padding: '20px 24px',
        borderRadius: '12px',
        bgcolor: REPORTS.welcome.bg,
      }}
    >
      <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Box
            component="span"
            sx={{
              padding: '4px 8px',
              borderRadius: '9999px',
              bgcolor: REPORTS.welcome.chipBg,
              color: REPORTS.welcome.chipFg,
              fontSize: 12,
              lineHeight: '16px',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
            }}
          >
            Sample
          </Box>
          <Typography
            id={titleId}
            component="h2"
            sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 700, color: COLORS.ink }}
          >
            {copy.title}
          </Typography>
        </Box>
        <Typography
          sx={{ maxWidth: 640, fontSize: 16, lineHeight: '24px', fontWeight: 500, color: COLORS.muted }}
        >
          {copy.body}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        <Box
          component="a"
          href={feedbackSurveyUrl(user?.id)}
          target="_blank"
          rel="noopener noreferrer"
          sx={TEXT_BUTTON_SX}
        >
          Send feedback
        </Box>
        {copy.action === 'getStarted' && (
          <Box
            component="a"
            href={GET_STARTED_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={recordSisRequest}
            sx={PRIMARY_BUTTON_SX}
          >
            Get Started
          </Box>
        )}
        {copy.action === 'uploadAccess' && (
          <Box component="button" type="button" onClick={onUploadAccess} sx={PRIMARY_BUTTON_SX}>
            <FileUploadOutlinedIcon sx={{ fontSize: 20 }} />
            <span>Upload ACCESS files</span>
          </Box>
        )}
      </Box>
    </Box>
  );
}
